import React, { useState, useEffect } from "react";
import { Form, Spinner } from "reactstrap";
import { useForm } from "react-hook-form";
import {
  ActionButton,
  BackButton,
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Col,
  FormCheckbox,
  FormDatePicker,
  PreviewAltCard,
  QATypeDropdown,
  Icon,
} from "../../../components/Component";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import { useHistory, useLocation } from "react-router-dom";
import { showSuccessToast, showErrorToast } from "../../../utils/Functions";
import { fileUploadApi } from "../carers/CarerData";
import { saveProgressReportAPI, getProgressReportDetailAPI } from "./ReportData";
import { bytesToMegaBytes, QATypes } from "../../../utils/Utils";
import moment from "moment";
import { iconsType } from "../../../layout/fileIcon/FileIcon";

const AddReport = () => {
  const history = useHistory();
  const location = useLocation();
  const [isEdit, setIsEdit] = useState(false);
  const [isListEmpty, setIsListEmpty] = useState(false);
  const [reportData, setReportData] = useState({
    id: 0,
    qaType: null,
    type: null,
    dueDate: null,
    completedDate: null,
    completedBy: "",
    commentsNotes: "",
    clientId: 0,
    carerId: 0,
    isRecursive: false,
  });
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [removeFileIds, setRemoveFileIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDisable, setIsDisable] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [isViewOnly, setIsViewOnly] = useState(false);

  useEffect(() => {
    // Handle clientId - check both clientId and data (for backward compatibility)
    const clientIdValue = location?.state?.clientId || location?.state?.data;
    const carerIdValue = location?.state?.carerId;
    const reportId = location?.state?.reportId;
    const isEditValue = location?.state?.isEdit;
    const isListEmptyValue = location?.state?.isListEmpty || false;
    const isViewOnlyValue = location?.state?.isViewOnly || false;

    setIsEdit(isEditValue || false);
    setIsListEmpty(isListEmptyValue);
    setIsViewOnly(isViewOnlyValue);

    // If editing, load report details
    if (isEditValue && reportId) {
      loadReportDetail(reportId);
    }

    // Automatically set type based on which ID exists (carerId takes precedence if both exist)
    let typeValue = null;
    if (carerIdValue) {
      typeValue = "CARER";
    } else if (clientIdValue) {
      typeValue = "CLIENT";
    }

    // Automatically set qaType based on which ID exists
    // If carerId exists → "Progress Report" (PROGRESS_REPORT)
    // If clientId exists → "Quality of Service" (QUALITY_OF_SERVICE)
    let qaTypeValue = null;
    if (carerIdValue) {
      qaTypeValue = QATypes.find((opt) => opt.value === "PROGRESS_REPORT");
    } else if (clientIdValue) {
      qaTypeValue = QATypes.find((opt) => opt.value === "QUALITY_OF_SERVICE");
    }

    setReportData((prev) => ({
      ...prev,
      clientId: clientIdValue || prev.clientId,
      carerId: carerIdValue || prev.carerId,
      type: typeValue || prev.type,
      qaType: qaTypeValue || prev.qaType,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const loadReportDetail = (reportId) => {
    setLoadingDetail(true);
    getProgressReportDetailAPI({ id: reportId }, (data, error) => {
      setLoadingDetail(false);
      if (error === null && data) {
        // Populate form with report data
        const report = data.data || data;

        // Set qaType
        const qaTypeValue = QATypes.find((opt) => opt.value === report?.qaType);

        // Set type
        const typeValue = report?.type || null;

        // Set dates
        const dueDate = report?.dueDate ? moment(report.dueDate).toDate() : null;
        const completedDate = report?.completedDate ? moment(report.completedDate).toDate() : null;

        // Set completedBy - handle both string and object formats
        let completedBy = "";
        if (report?.completedBy) {
          if (typeof report.completedBy === "string") {
            completedBy = report.completedBy;
          } else if (report.completedBy?.id) {
            completedBy = report.completedBy.id.toString();
          } else if (report.completedBy?.firstName || report.completedBy?.lastName) {
            completedBy = `${report.completedBy.firstName || ""} ${report.completedBy.lastName || ""}`.trim();
          }
        }

        // Handle attachments/files
        const existingFiles = (report?.attachments || []).map((file) => ({
          id: file.id,
          name: file.fileName || file.name || "File",
          fileUrl: file.fileUrl,
          mimeType: file.mimeType,
          isOldFile: true,
          isLoading: false,
        }));

        setReportData((prev) => ({
          ...prev,
          id: report?.id || 0,
          qaType: qaTypeValue || prev.qaType,
          type: typeValue || prev.type,
          dueDate: dueDate,
          completedDate: completedDate,
          completedBy: completedBy,
          commentsNotes: report?.comments || report?.comment || report?.commentsNotes || "",
          clientId: report?.clientData?.id || report?.clientId || prev.clientId,
          carerId: report?.carerData?.id || report?.carerId || prev.carerId,
          isRecursive: report?.isRecursive || false,
        }));

        setUploadedFiles(existingFiles);
      } else {
        showErrorToast(error?.message || "Failed to load report details");
      }
    });
  };

  React.useEffect(() => {
    // Type is automatically set based on clientId/carerId, so we don't need to check it
    let isInvalid = !reportData?.qaType || !reportData?.dueDate || (!reportData?.clientId && !reportData?.carerId);

    // If list was empty and Recursive is NOT selected, make Completed Date and Completed By mandatory
    if (isListEmpty && !isEdit && !reportData?.isRecursive) {
      isInvalid = isInvalid || !reportData?.completedDate || !reportData?.completedBy?.trim();
    }

    setIsDisable(isInvalid);
  }, [reportData, isListEmpty, isEdit]);

  const uploadFile = (file, index) => {
    fileUploadApi(file, "PROGRESS_REPORT", (data, error) => {
      setUploadedFiles((prevFiles) => {
        const updatedfiles = [...prevFiles];
        if (error === null) {
          file.id = data.id;
          file.isLoading = false;
          file.fileUrl = data.fileUrl;
          file.mimeType = data.mimeType;
          updatedfiles[index] = file;
        } else {
          updatedfiles.splice(index, 1);
          showErrorToast(error.message.length > 0 ? error.message : "File upload failed");
        }
        return updatedfiles;
      });
    });
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      files.forEach((file) => {
        if (bytesToMegaBytes(file.size) > 100) {
          showErrorToast("You can upload max 100 MB file");
          return;
        }
        const fileDetails = file;
        fileDetails.isLoading = true;
        fileDetails.isError = false;
        fileDetails.id = null;
        fileDetails.isOldFile = false;

        const updatedfiles = [...uploadedFiles];
        updatedfiles.push(fileDetails);
        setUploadedFiles(updatedfiles);

        // Upload file to get file ID
        uploadFile(fileDetails, updatedfiles.length - 1);
      });
    }
    e.target.value = "";
  };

  const handleFileRemove = (index) => {
    const updatedfiles = [...uploadedFiles];
    const fileToRemove = updatedfiles[index];

    if (fileToRemove?.id) {
      setRemoveFileIds([...removeFileIds, fileToRemove.id]);
    }

    updatedfiles.splice(index, 1);
    setUploadedFiles(updatedfiles);
  };

  const openFileInNewTab = (file) => {
    if (file?.fileUrl) {
      window.open(file.fileUrl, "_blank");
    }
  };

  const onFormSubmit = () => {
    // Prevent multiple API calls if already loading
    if (loading) {
      return;
    }

    setLoading(true);

    // Check if files are still uploading
    const filesStillUploading = uploadedFiles.some((file) => file.isLoading);
    if (filesStillUploading) {
      showErrorToast("Please wait for files to finish uploading");
      setLoading(false);
      return;
    }

    // Validate: If list was empty and Recursive is NOT selected, Completed Date and Completed By are mandatory
    if (isListEmpty && !isEdit && !reportData?.isRecursive) {
      if (!reportData?.completedDate) {
        showErrorToast("Completed Date is required");
        setLoading(false);
        return;
      }
      if (!reportData?.completedBy?.trim()) {
        showErrorToast("Completed By is required");
        setLoading(false);
        return;
      }
    } else if (!reportData?.isRecursive) {
      // Normal validation: If completedDate is filled, completedBy is mandatory (only when Recursive is not selected)
      if (reportData?.completedDate && !reportData?.completedBy?.trim()) {
        showErrorToast("Completed By is required when Completed Date is provided");
        setLoading(false);
        return;
      }
    }

    // Automatically determine type based on clientId or carerId
    let typeValue = reportData?.type || "";
    if (!typeValue) {
      if (reportData?.carerId) {
        typeValue = "CARER";
      } else if (reportData?.clientId) {
        typeValue = "CLIENT";
      }
    }

    // Automatically determine qaType based on clientId or carerId
    let qaTypeValue = reportData?.qaType?.value || "";
    if (!qaTypeValue) {
      if (reportData?.carerId) {
        qaTypeValue = "PROGRESS_REPORT";
      } else if (reportData?.clientId) {
        qaTypeValue = "QUALITY_OF_SERVICE";
      }
    }

    const fileIds = uploadedFiles.filter((file) => file.id && !file.isOldFile).map((file) => file.id);

    const params = {
      id: reportData?.id || undefined,
      carerId: reportData?.carerId || undefined,
      clientId: reportData?.clientId || undefined,
      qaType: qaTypeValue,
      type: typeValue,
      dueDate: reportData?.dueDate ? moment(reportData.dueDate).format("YYYY-MM-DD") : undefined,
      completedDate: reportData?.completedDate ? moment(reportData.completedDate).format("YYYY-MM-DD") : undefined,
      completedBy: reportData?.completedBy || undefined,
      comment: reportData?.commentsNotes || undefined,
      fileIds: fileIds,
      removeFileIds: removeFileIds,
      isRecursive: reportData?.isRecursive || false,
    };

    saveProgressReportAPI(params, (data, error) => {
      setLoading(false);
      if (error === null) {
        showSuccessToast(isEdit ? "Report updated successfully" : "Report added successfully");
        history.goBack();
      } else {
        showErrorToast(error.message || "Failed to save report");
      }
    });
  };

  const { handleSubmit } = useForm();

  return (
    <React.Fragment>
      <Head title={isViewOnly ? "View Report" : isEdit ? "Edit Report" : "Add Report"} />
      <Content>
        <Block className={"justify-center"}>
          <BlockHead size="md" wide="md">
            {loadingDetail ? (
              <div className="spinner">
                <Spinner size="sm" color="primary" />
              </div>
            ) : (
              <>
                <BlockBetween>
                  <BlockHeadContent>
                    <BlockTitle tag="h3" page>
                      {isViewOnly ? "View Report" : isEdit ? "Edit Report" : "Add Report"}
                    </BlockTitle>
                  </BlockHeadContent>
                  <BlockHeadContent>
                    <BackButton />
                  </BlockHeadContent>
                </BlockBetween>
                <PreviewAltCard className="mt-2">
                  <Form
                    className="row gy-4 form-validation"
                    onSubmit={(e) => {
                      e.preventDefault();
                      // Prevent form submission if already loading
                      if (!loading) {
                        handleSubmit(onFormSubmit)(e);
                      }
                    }}
                  >
                    <Col md="6">
                      <div className="form-group">
                        <label className="form-label">
                          Q&A Type<span className="text-danger fs-6">*</span>
                        </label>
                        <div className="form-control-wrap">
                          <QATypeDropdown
                            value={reportData?.qaType}
                            onSelect={(selected) => {
                              setReportData({ ...reportData, qaType: selected });
                            }}
                            placeholder="Select Q&A Type"
                            disable={true}
                          />
                        </div>
                      </div>
                    </Col>
                    <Col md="6">
                      <div className="form-group">
                        <label className="form-label">
                          Due Date<span className="text-danger fs-6">*</span>
                        </label>
                        <FormDatePicker
                          selected={reportData?.dueDate}
                          onChange={(dueDate) => {
                            setReportData((prev) => ({
                              ...prev,
                              dueDate: dueDate,
                            }));
                          }}
                          disabled={isViewOnly}
                        />
                      </div>
                    </Col>
                    {!isEdit && (
                      <Col md="12">
                        <div className="form-group">
                          <FormCheckbox
                            id="isRecursive"
                            name="isRecursive"
                            label="Recursive"
                            checked={reportData?.isRecursive || false}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              if (isChecked) {
                                // Clear fields when Recursive is checked
                                setReportData({
                                  ...reportData,
                                  isRecursive: true,
                                  completedDate: null,
                                  completedBy: "",
                                  commentsNotes: "",
                                });
                                // Clear uploaded files
                                setUploadedFiles([]);
                                setRemoveFileIds([]);
                              } else {
                                setReportData({ ...reportData, isRecursive: false });
                              }
                            }}
                            disabled={isViewOnly}
                          />
                        </div>
                      </Col>
                    )}
                    <Col md="6">
                      <div className="form-group">
                        <label className="form-label">
                          Completed Date
                          {isListEmpty && !isEdit && !reportData?.isRecursive && (
                            <span className="text-danger fs-6">*</span>
                          )}
                        </label>
                        <FormDatePicker
                          selected={reportData?.completedDate}
                          onChange={(completedDate) => {
                            setReportData((prev) => ({
                              ...prev,
                              completedDate: completedDate,
                            }));
                          }}
                          disabled={isViewOnly || reportData?.isRecursive}
                        />
                      </div>
                    </Col>
                    <Col md="6">
                      <div className="form-group">
                        <label className="form-label">
                          Completed By
                          {isListEmpty && !isEdit && !reportData?.isRecursive && (
                            <span className="text-danger fs-6">*</span>
                          )}
                        </label>
                        <div className="form-control-wrap">
                          <input
                            className="form-control"
                            type="text"
                            placeholder="Enter Completed By"
                            value={reportData?.completedBy}
                            onChange={(e) => {
                              setReportData({ ...reportData, completedBy: e.target.value });
                            }}
                            disabled={isViewOnly || reportData?.isRecursive}
                          />
                        </div>
                      </div>
                    </Col>
                    <Col md="12">
                      <div className="form-group">
                        <label className="form-label">Comments/Notes</label>
                        <div className="form-control-wrap">
                          <textarea
                            className="form-control"
                            placeholder="Enter Comments/Notes"
                            value={reportData?.commentsNotes}
                            onChange={(e) => {
                              setReportData({ ...reportData, commentsNotes: e.target.value });
                            }}
                            maxLength={200}
                            disabled={isViewOnly || reportData?.isRecursive}
                          />
                          <span className="d-flex justify-content-end pt-1">
                            {reportData?.commentsNotes?.length || 0}/200
                          </span>
                        </div>
                      </div>
                    </Col>
                    <Col md="12">
                      {!isViewOnly && (
                        <div className="form-group mb-0">
                          <label
                            htmlFor="attachments"
                            style={{
                              cursor: reportData?.isRecursive ? "not-allowed" : "pointer",
                              opacity: reportData?.isRecursive ? 0.6 : 1,
                            }}
                            onClick={(e) => {
                              if (reportData?.isRecursive) {
                                e.preventDefault();
                              }
                            }}
                          >
                            <Icon name="clip" className="mb-1 text-primary" />
                            <span className="text-primary"> Attachments</span>
                          </label>
                          <input
                            type="file"
                            name="attachments"
                            accept=".pdf,.doc,.docx,.xlsx,.xls,.jpg,.jpeg,.png"
                            className="form-control"
                            id="attachments"
                            style={{ display: "none" }}
                            onChange={handleFileSelect}
                            multiple
                            disabled={reportData?.isRecursive}
                          />
                        </div>
                      )}
                      {isViewOnly && uploadedFiles?.length > 0 && (
                        <div className="form-group mb-0">
                          <label>
                            <Icon name="clip" className="mb-1 text-primary" />
                            <span className="text-primary"> Attachments</span>
                          </label>
                        </div>
                      )}
                      {uploadedFiles?.length > 0 &&
                        uploadedFiles?.map((file, ind) => {
                          return (
                            <div key={`file-${ind}`} className="nk-upload-item mt-3">
                              <div className="nk-upload-icon">
                                {file?.isOldFile ? (
                                  <>{iconsType[file?.mimeType] ? iconsType[file?.mimeType] : iconsType["others"]}</>
                                ) : (
                                  <>{iconsType[file?.type] ? iconsType[file?.type] : iconsType["others"]}</>
                                )}
                              </div>
                              <div className="nk-upload-info">
                                <div className="nk-upload-title">
                                  <span
                                    className="title"
                                    style={{ cursor: "pointer" }}
                                    onClick={() => openFileInNewTab(file)}
                                  >
                                    {file?.name}
                                  </span>
                                </div>
                              </div>
                              {!isViewOnly && (
                                <div className="nk-upload-action">
                                  <a
                                    href="#delete"
                                    onClick={(ev) => {
                                      ev.preventDefault();
                                    }}
                                    className="btn btn-icon btn-trigger round-40 d-flex justify-content-center align-items-center"
                                    style={{
                                      opacity: reportData?.isRecursive ? 0.5 : 1,
                                      cursor: reportData?.isRecursive ? "not-allowed" : "pointer",
                                    }}
                                  >
                                    {file?.isLoading ? (
                                      <Spinner size="sm" color="dark" />
                                    ) : (
                                      <span
                                        onClick={(e) => {
                                          if (!reportData?.isRecursive) {
                                            handleFileRemove(ind);
                                          } else {
                                            e.preventDefault();
                                          }
                                        }}
                                      >
                                        <Icon name="trash"></Icon>
                                      </span>
                                    )}
                                  </a>
                                </div>
                              )}
                            </div>
                          );
                        })}
                    </Col>
                    {!isViewOnly && (
                      <Col md="12">
                        <ul className="align-center justify-content-end flex-wrap flex-sm-nowrap gx-4 gy-2">
                          <li>
                            <BackButton cancel />
                          </li>
                          <li>
                            <ActionButton title="Save" type="submit" loading={loading} disabled={isDisable} />
                          </li>
                        </ul>
                      </Col>
                    )}
                  </Form>
                </PreviewAltCard>
              </>
            )}
          </BlockHead>
        </Block>
      </Content>
    </React.Fragment>
  );
};

export default AddReport;
